import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  InfiniteData,
} from '@tanstack/react-query';
import { QUERY_KEYS } from 'service/querykeys';
import { Notification, NotificationFilters, SSENotificationEvent } from 'types/notifications';
import { useAppSelector } from 'store';
import {
  getNotifications,
  getNotificationStats,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  clearAllNotifications,
  bulkMarkNotificationsAsRead,
  bulkDeleteNotifications,
} from './api';

type NotifPage = Awaited<ReturnType<typeof getNotifications>>;

// ─── List (infinite) ──────────────────────────────────────────────────────────

export const useNotificationsQuery = (
  filters?: Omit<NotificationFilters, "page">,
) => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useInfiniteQuery<NotifPage, Error, InfiniteData<NotifPage>>({
    queryKey: [...QUERY_KEYS.NOTIFICATIONS, accessToken, filters],
    queryFn: ({ pageParam = 0 }) =>
      getNotifications({ ...filters, page: pageParam as number, size: 20 }),
    getNextPageParam: (lastPage) => {
      const next = lastPage.currentPage + 1;
      return next < lastPage.totalPages ? next : undefined;
    },
    initialPageParam: 0,
    staleTime: 30_000,
    enabled: !!accessToken,
  });
};

// ─── Stats ────────────────────────────────────────────────────────────────────

export const useNotificationStatsQuery = () => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useQuery({
    queryKey: [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, "stats", accessToken],
    queryFn: getNotificationStats,
    staleTime: 15_000,
    enabled: !!accessToken,
  });
};

// ─── Mark single as read ──────────────────────────────────────────────────────

export const useMarkAsReadMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuid: string) => markNotificationAsRead(uuid),
    onMutate: async (uuid) => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      const statsQueryKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<NotifPage>>(queryKey);
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) =>
                n.uuid === uuid ? { ...n, isRead: true } : n,
              ),
            })),
          };
        },
      );
      // Decrement unread count optimistically
      queryClient.setQueryData<any>(
        statsQueryKey,
        (stats: any) => stats ? { ...stats, unread: Math.max(0, stats.unread - 1) } : stats,
      );
      return { prev };
    },
    onError: (_err, _uuid, ctx) => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      const statsQueryKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];
      if (ctx?.prev) {
        queryClient.setQueryData(queryKey, ctx.prev);
      }
      queryClient.invalidateQueries({ queryKey: statsQueryKey });
    },
  });
};

// ─── Mark all as read ─────────────────────────────────────────────────────────

export const useMarkAllAsReadMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: markAllNotificationsAsRead,
    onMutate: async () => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      const statsQueryKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) => ({ ...n, isRead: true })),
            })),
          };
        },
      );
      queryClient.setQueryData<any>(statsQueryKey, (stats: any) => stats ? { ...stats, unread: 0 } : stats);
    },
    onError: () => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      const statsQueryKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: statsQueryKey });
    },
  });
};

// ─── Delete single ────────────────────────────────────────────────────────────

export const useDeleteNotificationMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuid: string) => deleteNotification(uuid),
    onMutate: async (uuid) => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      const statsQueryKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<NotifPage>>(queryKey);
      // Remove from cache
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          let wasUnread = false;
          const pages = old.pages.map((page) => ({
            ...page,
            data: page.data.filter((n) => {
              if (n.uuid === uuid) {
                if (!n.isRead) wasUnread = true;
                return false;
              }
              return true;
            }),
          }));
          if (wasUnread) {
            queryClient.setQueryData<any>(
              statsQueryKey,
              (stats: any) => stats ? { ...stats, unread: Math.max(0, stats.unread - 1) } : stats,
            );
          }
          return { ...old, pages };
        },
      );
      return { prev };
    },
    onError: (_err, _uuid, ctx) => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      if (ctx?.prev) {
        queryClient.setQueryData(queryKey, ctx.prev);
      }
    },
  });
};

// ─── Clear all ────────────────────────────────────────────────────────────────

export const useClearAllNotificationsMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: clearAllNotifications,
    onMutate: async () => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      const statsQueryKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return { ...old, pages: [{ ...old.pages[0], data: [], totalElements: 0, totalPages: 0 }] };
        },
      );
      queryClient.setQueryData<any>(statsQueryKey, (stats: any) => stats ? { ...stats, unread: 0, total: 0, mentions: 0 } : stats);
    },
    onError: () => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      const statsQueryKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: statsQueryKey });
    },
  });
};

// ─── Bulk Mark As Read ────────────────────────────────────────────────────────

export const useBulkMarkAsReadMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuids: string[]) => bulkMarkNotificationsAsRead(uuids),
    onMutate: async (uuids) => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<NotifPage>>(queryKey);
      
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) =>
                uuidSet.has(n.uuid) ? { ...n, isRead: true } : n,
              ),
            })),
          };
        },
      );
      return { prev };
    },
    onError: (err, _, context) => {
      if (context?.prev) {
        queryClient.setQueryData([...QUERY_KEYS.NOTIFICATIONS, accessToken], context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken] });
    },
  });
};

// ─── Bulk Delete ──────────────────────────────────────────────────────────────

export const useBulkDeleteMutation = () => {
  const queryClient = useQueryClient();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  return useMutation({
    mutationFn: (uuids: string[]) => bulkDeleteNotifications(uuids),
    onMutate: async (uuids) => {
      const queryKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<InfiniteData<NotifPage>>(queryKey);
      
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey, exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.filter((n) => !uuidSet.has(n.uuid)),
            })),
          };
        },
      );
      return { prev };
    },
    onError: (err, _, context) => {
      if (context?.prev) {
        queryClient.setQueryData([...QUERY_KEYS.NOTIFICATIONS, accessToken], context.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, accessToken] });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken] });
    },
  });
};

// ─── SSE Cache Handler ────────────────────────────────────────────────────────
// Called by NotificationSSEProvider when module === 'PMO'

export const applyPmoSSEEvent = (
  queryClient: ReturnType<typeof useQueryClient>,
  accessToken: string | null,
  event: SSENotificationEvent,
) => {
  const listKey = [...QUERY_KEYS.NOTIFICATIONS, accessToken];
  const statsKey = [...QUERY_KEYS.NOTIFICATIONS_UNREAD_COUNT, 'stats', accessToken];

  switch (event.action) {
    case 'CREATED': {
      const newNotif = event.data as Notification;
      if (!newNotif?.uuid) break;

      // Invalidate relevant caches based on notification type so that navigating
      // to the resource fetches fresh data (preventing "Not found" scrolling bugs
      // and showing stale status/assignee data).
      switch (newNotif.notificationType) {
        case 'MENTIONED_IN_COMMENT':
        case 'COMMENT_REACTION':
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMMENTS] });
          queryClient.invalidateQueries({ queryKey: ['comment-replies'] });
          break;
        case 'TIME_LOGGED':
        case 'TASK_ASSIGNED':
        case 'TASK_PRIORITY_UPDATED':
        case 'TASK_STATUS_UPDATED':
        case 'TASK_ASSIGNEE_UPDATED':
        case 'MENTIONED_IN_TASK':
          queryClient.invalidateQueries({ queryKey: ['task'] }); // Task details & work-logs
          queryClient.invalidateQueries({ queryKey: ['tasks'] }); // Board/List views
          queryClient.invalidateQueries({ queryKey: ['task-children-infinite'] }); // Children list view
          break;
        case 'WORK_ITEM_CREATED':
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
          break;
        case 'PROJECT_INVITE':
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          break;
      }

      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey: listKey, exact: false },
        (old) => {
          if (!old) return old;
          const allExisting = old.pages.flatMap((p) => p.data);
          if (allExisting.some((n) => n.uuid === newNotif.uuid)) return old;
          const firstPage = old.pages[0];
          return {
            ...old,
            pages: [
              { ...firstPage, data: [newNotif, ...firstPage.data] },
              ...old.pages.slice(1),
            ],
          };
        },
      );
      if (!newNotif.isRead) {
        queryClient.setQueryData<any>(
          statsKey,
          (stats: any) => stats ? { ...stats, unread: (stats.unread ?? 0) + 1 } : stats,
        );
      }
      break;
    }

    case 'READ': {
      const uuids = event.notificationUuids ??
        (event.notificationUuid ? [event.notificationUuid] : []);
      if (!uuids.length) break;
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey: listKey, exact: false },
        (old) => {
          if (!old) return old;
          let markedCount = 0;
          const pages = old.pages.map((page) => ({
            ...page,
            data: page.data.map((n) => {
              if (uuidSet.has(n.uuid) && !n.isRead) { markedCount++; return { ...n, isRead: true }; }
              return n;
            }),
          }));
          if (markedCount > 0) {
            queryClient.setQueryData<any>(
              statsKey,
              (stats: any) => stats ? { ...stats, unread: Math.max(0, (stats.unread ?? 0) - markedCount) } : stats,
            );
          }
          return { ...old, pages };
        },
      );
      break;
    }

    case 'READ_ALL':
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.setQueryData<any>(statsKey, (stats: any) => stats ? { ...stats, unread: 0 } : stats);
      break;

    case 'DELETED': {
      const uuids = event.notificationUuids ??
        (event.notificationUuid ? [event.notificationUuid] : []);
      if (!uuids.length) break;
      const uuidSet = new Set(uuids);
      queryClient.setQueriesData<InfiniteData<NotifPage>>(
        { queryKey: listKey, exact: false },
        (old) => {
          if (!old) return old;
          let removedUnread = 0;
          const pages = old.pages.map((page) => ({
            ...page,
            data: page.data.filter((n) => {
              if (uuidSet.has(n.uuid)) { if (!n.isRead) removedUnread++; return false; }
              return true;
            }),
          }));
          if (removedUnread > 0) {
            queryClient.setQueryData<any>(
              statsKey,
              (stats: any) => stats ? { ...stats, unread: Math.max(0, (stats.unread ?? 0) - removedUnread) } : stats,
            );
          }
          return { ...old, pages };
        },
      );
      break;
    }

    case 'CLEARED':
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.setQueryData<any>(statsKey, (stats: any) => stats ? { ...stats, unread: 0, total: 0 } : stats);
      break;
  }
};
